import { buildBeforeAfterPoCell } from "@/features/results/beforeAfterPoCell.ts";
import {
  calculateUrgency,
  computeDaysToStockout,
  computeWeeksOfStock,
} from "@/features/results/resultHelpers.ts";
import type { BulkOrderDateContext } from "@/redistribution/models/BulkOrderDateContext.ts";
import type { MovementGridRow } from "@/redistribution/models/MovementGridRow.ts";
import type { PoDetailGridSkuRow } from "@/redistribution/models/PoDetailGridSkuRow.ts";
import type { Sku } from "@/redistribution/models/Sku.ts";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { PurchaseOrder } from "../redistribution/models/PurchaseOrder.js";
import type { RedistributionResult } from "../redistribution/models/RedistributionResult.js";
import {
  afterQty as afterQtyHelper,
  beforeQty as beforeQtyHelper,
  representativeSku as representativeSkuHelper,
  skuOnPurchaseOrder,
} from "./modules/redistributionHelpers.ts";

type BeforeAfterView = "summary" | "details";

export const useRedistributionStore = defineStore("redistribution", () => {
  const redistributionResult = ref<RedistributionResult | undefined>(undefined);
  const originalPOs = ref<PurchaseOrder[]>([]);
  const traceLog = ref<string | undefined>(undefined);
  const activeRequestId = ref<string | undefined>(undefined);
  const beforeAfterView = ref<BeforeAfterView>("summary");
  // Empty for regular PO runs and for historical runs loaded without a queue item.
  const bulkOrderDates = ref<BulkOrderDateContext[]>([]);

  function setTraceLog(content: string | undefined): void {
    traceLog.value = content;
  }

  function setBulkOrderDates(contexts: BulkOrderDateContext[] = []): void {
    bulkOrderDates.value = contexts;
  }
  const updatedPOs = computed<PurchaseOrder[]>(
    () => redistributionResult.value?.updatedPurchaseOrderList ?? []
  );

  const _allSkuNumbers = computed<string[]>(() => {
    const set = new Set<string>();
    for (const po of [...originalPOs.value, ...updatedPOs.value]) {
      for (const s of po.skus) {
        set.add(s.skuNumber);
      }
    }
    return [...set];
  });

  const beforeAfterGridRowData = computed(() => {
    const skus = _allSkuNumbers.value.toSorted((a, b) => {
      const sa = representativeSkuHelper(a, updatedPOs.value);
      const sb = representativeSkuHelper(b, updatedPOs.value);
      return (sa?.daysToStockout ?? 999) - (sb?.daysToStockout ?? 999);
    });
    const returnValue = skus.map((skuNumber) => {
      const info = representativeSkuHelper(skuNumber, updatedPOs.value);
      const daysToStockout = info?.daysToStockout ?? 999;
      const dailyRateOfSale = info?.dailyRateOfSale ?? 0;
      const row: Record<string, unknown> = {
        sku: skuNumber,
        description: info?.description ?? "Unknown",
        daysToStockout,
        dailyRateOfSale,
        totalBefore: originalPOs.value.reduce(
          (s, po) =>
            s +
            beforeQtyHelper(
              skuNumber,
              po.purchaseOrderNumber,
              originalPOs.value
            ),
          0
        ),
        totalAfter: updatedPOs.value.reduce(
          (s, po) =>
            s +
            afterQtyHelper(skuNumber, po.purchaseOrderNumber, updatedPOs.value),
          0
        ),
        urgency: calculateUrgency(daysToStockout, dailyRateOfSale),
      };

      for (const po of updatedPOs.value) {
        const poNumber = po.purchaseOrderNumber;
        const originalSku = skuOnPurchaseOrder(
          skuNumber,
          poNumber,
          originalPOs.value
        );
        const updatedSku = skuOnPurchaseOrder(
          skuNumber,
          poNumber,
          updatedPOs.value
        );
        const projectedStock =
          originalSku?.extraUnits ?? updatedSku?.extraUnits ?? 0;
        const dailyRateOfSale =
          updatedSku?.dailyRateOfSale ??
          originalSku?.dailyRateOfSale ??
          info?.dailyRateOfSale ??
          0;
        const beforeQty = beforeQtyHelper(
          skuNumber,
          poNumber,
          originalPOs.value
        );
        const afterQty = afterQtyHelper(skuNumber, poNumber, updatedPOs.value);
        row[`${poNumber}_before`] = buildBeforeAfterPoCell({
          projectedStock,
          poQty: beforeQty,
          dailyRateOfSale,
        });
        row[`${poNumber}_after`] = buildBeforeAfterPoCell({
          projectedStock,
          poQty: afterQty,
          dailyRateOfSale,
        });
      }
      return row;
    });
    return returnValue;
  });
  const itemMovementsGridRowData = computed<MovementGridRow[]>(() => {
    // If the API returned server-computed movements, use them directly
    const apiMovements = redistributionResult.value?.movements;
    const rateOfSaleDays = redistributionResult.value?.rateOfSaleDays ?? null;
    if (apiMovements && apiMovements.length > 0) {
      const rows = apiMovements.map((m) => {
        const skuInfo = representativeSkuHelper(m.skuNumber, updatedPOs.value);
        return {
          sku: m.skuNumber,
          description: skuInfo?.description ?? "Unknown",
          ...(skuInfo?.theme ? { collection: skuInfo.theme } : {}),
          ...(skuInfo?.color ? { color: skuInfo.color } : {}),
          from: m.fromPurchaseOrder,
          to: m.toPurchaseOrder,
          qty: m.quantityMoved,
          reason: m.reason,
          wosChange: (m.wosChangeWeeks as number | null) ?? null,
          wosAverage: (m.wosAverageWeeks as number | null) ?? null,
          wosBefore: (m.wosBeforeWeeks as number | null) ?? null,
          wosAfter: (m.wosAfterWeeks as number | null) ?? null,
          daysToStockoutBefore:
            (m.daysToStockoutBefore as number | null) ?? null,
          daysToStockoutAfter: (m.daysToStockoutAfter as number | null) ?? null,
          rateOfSaleDays,
          // PO popover data
          fromWarehouse: m.fromWarehouse,
          fromDivision: m.fromDivision,
          toWarehouse: m.toWarehouse,
          toDivision: m.toDivision,
          fromCubesBefore:
            originalPOs.value
              .find((p) => p.purchaseOrderNumber === m.fromPurchaseOrder)
              ?.skus.reduce(
                (sum, s) => sum + s.lineItemQuantity * s.cubeSize,
                0
              ) ?? null,
          fromCubesAfter:
            updatedPOs.value
              .find((p) => p.purchaseOrderNumber === m.fromPurchaseOrder)
              ?.skus.reduce(
                (sum, s) => sum + s.lineItemQuantity * s.cubeSize,
                0
              ) ?? null,
          toCubesBefore:
            originalPOs.value
              .find((p) => p.purchaseOrderNumber === m.toPurchaseOrder)
              ?.skus.reduce(
                (sum, s) => sum + s.lineItemQuantity * s.cubeSize,
                0
              ) ?? null,
          toCubesAfter:
            updatedPOs.value
              .find((p) => p.purchaseOrderNumber === m.toPurchaseOrder)
              ?.skus.reduce(
                (sum, s) => sum + s.lineItemQuantity * s.cubeSize,
                0
              ) ?? null,
        };
      });

      // Merge rows with the same SKU + From PO + To PO + Reason.
      const merged = new Map<string, (typeof rows)[0]>();
      for (const row of rows) {
        const key = `${row.sku}|${row.from}|${row.to}|${row.reason}`;
        const existing = merged.get(key);
        if (existing) {
          existing.qty += row.qty;
          if (row.wosChange != null) {
            existing.wosChange =
              Math.round(((existing.wosChange ?? 0) + row.wosChange) * 100) /
              100;
          }
          // After values update to the latest move; before values stay from the first.
          if (row.wosAfter != null) existing.wosAfter = row.wosAfter;
          if (row.daysToStockoutAfter != null)
            existing.daysToStockoutAfter = row.daysToStockoutAfter;
        } else {
          merged.set(key, { ...row });
        }
      }
      return [...merged.values()];
    }
    // UI is intentionally driven by algorithm-result movements only.
    return [];
  });
  const poDetailGridRowDatas = computed<Record<string, PoDetailGridSkuRow[]>>(
    () => {
      const rows: Record<string, PoDetailGridSkuRow[]> = {};
      if (updatedPOs.value.length === 0) return rows;
      for (const po of updatedPOs.value) {
        const returnVal = po.skus.map((sku: Sku) => {
          const difference = sku.difference ?? 0;
          const extra = sku.extraUnits ?? 0;
          const afterWos = computeWeeksOfStock(
            sku.lineItemQuantity + extra,
            sku.dailyRateOfSale
          );
          const beforeWos = computeWeeksOfStock(
            sku.lineItemQuantity - difference + extra,
            sku.dailyRateOfSale
          );
          const skuRow: PoDetailGridSkuRow = {
            sku: sku.skuNumber,
            collection: sku.theme,
            description: sku.description,
            color: sku.color,
            quantityAvailable: sku.quantityAvailable,
            forecastQuantityAvailable: sku.forecastQuantityAvailable,
            adjustedForecast:
              sku.adjustedForecast ?? sku.lineItemQuantity + extra,
            dailyRateOfSale: sku.dailyRateOfSale,
            quantity: sku.lineItemQuantity,
            previousQuantity: sku.lineItemQuantity - difference,
            daysToStockout: afterWos.daysToStockout,
            weeksOfStock: afterWos.weeksOfStock,
            previousDaysToStockout: beforeWos.daysToStockout,
            previousWeeksOfStock: beforeWos.weeksOfStock,
            difference,
          };
          return skuRow;
        });
        rows[po.purchaseOrderNumber] = returnVal;
      }
      return rows;
    }
  );

  function setResult(
    r: RedistributionResult,
    originals: PurchaseOrder[],
    requestId?: string
  ) {
    const extraByPoSku = new Map<string, number>();
    const engineSkuByPoSku = new Map<string, Sku>();
    for (const po of r.updatedPurchaseOrderList) {
      for (const sku of po.skus) {
        extraByPoSku.set(
          `${po.purchaseOrderNumber}|${sku.skuNumber}`,
          sku.extraUnits ?? 0
        );
        engineSkuByPoSku.set(`${po.purchaseOrderNumber}|${sku.skuNumber}`, sku);
        sku.daysToStockout = computeDaysToStockout(
          sku.lineItemQuantity + (sku.extraUnits ?? 0),
          sku.dailyRateOfSale
        );
        sku.urgency = calculateUrgency(sku.daysToStockout, sku.dailyRateOfSale);
      }
    }

    for (const po of originals) {
      for (const sku of po.skus) {
        const key = `${po.purchaseOrderNumber}|${sku.skuNumber}`;
        const engineSku = engineSkuByPoSku.get(key);
        sku.extraUnits = extraByPoSku.get(key) ?? 0;
        if (engineSku) {
          sku.dailyRateOfSale = engineSku.dailyRateOfSale;
          sku.quantityAvailable = engineSku.quantityAvailable;
          sku.forecastQuantityAvailable = engineSku.forecastQuantityAvailable;
          sku.adjustedForecast = engineSku.adjustedForecast ?? 0;
          sku.requiredPerMonth = engineSku.requiredPerMonth ?? 0;
          sku.requiredPerRange = engineSku.requiredPerRange ?? 0;
        }
        sku.daysToStockout = computeDaysToStockout(
          sku.lineItemQuantity + sku.extraUnits,
          sku.dailyRateOfSale
        );
        sku.urgency = calculateUrgency(sku.daysToStockout, sku.dailyRateOfSale);
      }
    }

    redistributionResult.value = r;
    originalPOs.value = originals;
    activeRequestId.value = requestId;
  }

  const lastCustomRequestBody = ref<string | undefined>(undefined);

  function setLastCustomRequestBody(body: string): void {
    lastCustomRequestBody.value = body;
  }

  function clearResult() {
    redistributionResult.value = undefined;
    originalPOs.value = [];
    traceLog.value = undefined;
    activeRequestId.value = undefined;
    bulkOrderDates.value = [];
  }

  return {
    redistributionResult,
    originalPOs,
    updatedPOs,
    setResult,
    clearResult,
    traceLog,
    setTraceLog,
    bulkOrderDates,
    setBulkOrderDates,
    activeRequestId,
    lastCustomRequestBody,
    setLastCustomRequestBody,
    beforeAfterView,
    beforeAfterGridRowData,
    itemMovementsGridRowData,
    poDetailGridRowDatas,
  };
});
